/**
 * Paddle wordmark inlined so it inherits `currentColor` from the
 * surrounding text — the external SVG had a hardcoded near-black fill
 * that vanished on the dark (default) theme.
 */
function PaddleLogo({ className = '' }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 432 135'
      fill='currentColor'
      aria-hidden='true'
      className={className}
    >
      <path d='M0,68.1v-3.6c4.4,0,8.7-1.7,11.8-4.9c3.1-3.1,4.9-7.3,4.9-11.7H20c0,4.4,1.8,8.6,4.9,11.7 c3.1,3.1,7.4,4.9,11.8,4.9v3.6c-4.4,0-8.7,1.7-11.8,4.9c-3.1,3.1-4.9,7.3-4.9,11.7h-3.3c0-4.4-1.8-8.6-4.9-11.7 C8.6,69.9,4.4,68.1,0,68.1z M16.7,41.7h28.7c14,0,24,9.9,24,24.5s-10,24.5-24,24.5H16.7v41.8h12v-31.2h16.7 c20.2,0,36.1-15.8,36.1-35.1c0-19.4-15.9-35.1-36.1-35.1H16.7C16.7,31.1,16.7,41.7,16.7,41.7z M124.6,31 c10.5,0,19.9,5.3,25.8,13.7V33.2h12v66.3h-12V87.9c-6,8.4-15.4,13.7-25.8,13.7c-19.8,0-36.5-15.5-36.5-35.3S104.8,31,124.6,31z M124.6,90.9c15.5,0,25.8-10.6,25.8-24.7c0-14.2-10.1-24.7-25.8-24.7c-13.9,0-24.4,9.9-24.4,24.7C100.1,81,110.7,90.9,124.6,90.9 z M206.8,31c10.6,0,19.9,5.3,25.8,13.7V0h12v99.4h-12V87.9c-5.9,8.4-15.2,13.7-25.8,13.7c-19.8,0-36.5-15.5-36.5-35.3 C170.4,46.5,187,31,206.8,31z M206.8,90.9c15.6,0,25.8-10.5,25.8-24.7s-10.2-24.7-25.8-24.7c-13.9,0-24.4,9.9-24.4,24.7 S192.9,90.9,206.8,90.9z M290.4,31c10.6,0,19.9,5.3,25.8,13.7V0h12v99.4h-12V87.9c-5.9,8.4-15.2,13.7-25.8,13.7 c-19.8,0-36.5-15.5-36.5-35.3C254,46.5,270.6,31,290.4,31z M290.4,90.9c15.6,0,25.8-10.5,25.8-24.7S306,41.5,290.4,41.5 c-13.9,0-24.4,9.9-24.4,24.7S276.5,90.9,290.4,90.9z M340.2,99.4V0h12v99.4H340.2z M396.7,31.1c23.1,0,34.2,17.9,34.2,39.8h-57.7 c1.9,12.9,11.5,21.2,23.5,21.2c8.3,0,14.7-3.7,19.5-11h13c-4.5,10.2-16.4,20.3-32.4,20.3c-20.2,0-35.8-16-35.8-35.1 C360.9,47.2,376.5,31.1,396.7,31.1L396.7,31.1z M418.9,61.6c0-10.9-9.3-21.2-22.2-21.2c-12,0-21.6,8.4-23.5,21.2H418.9z' />
    </svg>
  )
}

/**
 * Merchant-of-Record trust badge. Use anywhere a reader is about to see
 * a price, enter a checkout, or read payment/refund terms. Keeps the
 * Paddle relationship visually consistent without re-typing markup.
 */
export function PoweredByPaddle({
  variant = 'inline',
  className = '',
}: {
  variant?: 'inline' | 'card'
  className?: string
}) {
  if (variant === 'card') {
    return (
      <div
        className={`inline-flex items-center gap-3 border border-border bg-card/70 px-4 py-3 rounded-lg ${className}`}
      >
        <PaddleLogo className='h-5 w-auto text-foreground opacity-90' />
        <div className='flex flex-col leading-tight'>
          <span className='text-[11px] font-semibold text-foreground'>
            Merchant of Record
          </span>
          <span className='text-[10px] text-muted-foreground'>
            Tax, compliance, fraud protection handled by Paddle
          </span>
        </div>
      </div>
    )
  }

  return (
    <a
      href='https://paddle.com'
      target='_blank'
      rel='noopener noreferrer'
      className={`inline-flex items-center gap-2 text-[11px] text-muted-foreground hover:text-foreground transition-colors ${className}`}
    >
      <span>Payments secured by</span>
      <PaddleLogo className='h-4 w-auto opacity-80' />
      <span>· Merchant of Record</span>
    </a>
  )
}
